import _ from 'lodash'

/**
 * Decide quais atividades a execução consegue de fato apresentar.
 *
 * Existe porque a API emite atividades que o frontend não tem como jogar, e até
 * agora elas eram **descartadas em silêncio** — o filtro por kind conhecido
 * vivia dentro do `getSteps` de cada domínio, sem contagem e sem motivo.
 *
 * O silêncio custava caro. O `hangman` existe no enum da API (18 registros na
 * demo) e não tem componente: some da lista de steps, mas continua sendo o
 * "último desafio não concluído" que a API devolve. Na retomada, o nível
 * procurava o step dele, não achava, e caía no step inicial — um "Continuar"
 * que não continuava nada, sem erro no console e sem pista para o participante.
 *
 * A mesma armadilha vale para atividade de kind conhecido e conteúdo vazio: um
 * `slide` sem `meta.slides` não gera step nenhum, com o mesmo efeito.
 *
 * Aqui as duas viram o mesmo conceito — atividade **não jogável** — com motivo
 * nomeado, para que a navegação possa pular e a interface possa contabilizar.
 */

export type SkipReason =
  /** O kind não tem componente no catálogo compartilhado. */
  | 'kind_sem_componente'
  /** O kind é conhecido, mas falta o conteúdo mínimo para montar a atividade. */
  | 'sem_conteudo'

export interface UnplayableActivity {
  id: number | string
  kind: string
  reason: SkipReason
}

export interface PlayabilityInput {
  id: number | string
  kind: string
  meta?: Record<string, unknown> | null
}

export interface PlayabilityReport<T> {
  playable: T[]
  /** As descartadas, com o motivo — é o que permite contabilizar em vez de sumir. */
  unplayable: UnplayableActivity[]
}

const isFilledArray = (value: unknown) => Array.isArray(value) && value.length > 0

const isFilledString = (value: unknown) =>
  typeof value === 'string' && value.trim() !== ''

/**
 * Conteúdo mínimo por kind — cada regra espelha o que o componente daquele kind
 * de fato lê. Alterar um componente sem alterar a regra daqui volta a produzir
 * atividade que monta vazia, então as duas coisas andam juntas.
 *
 * Kind ausente desta tabela é considerado jogável: a regra só existe onde há um
 * conteúdo obrigatório verificável, e o silêncio aqui é deliberado — errar para
 * o lado de mostrar é melhor do que esconder atividade boa.
 *
 * As chaves são os **slugs literais**, não `KINDS`. Importar o catálogo aqui
 * fecha um ciclo — o catálogo carrega os componentes, que importam a barrel do
 * motor, que reexporta este módulo — e `KINDS` chega `undefined` na inicializacao.
 * O slug é o valor que a API emite: é o contrato, e não muda com o catálogo.
 */
const HAS_CONTENT: Record<string, (meta: any) => boolean> = {
  slide: meta => isFilledArray(_.get(meta, 'slides')),
  stories: meta => isFilledArray(_.get(meta, 'stories')),
  memory: meta => isFilledArray(_.get(meta, 'cards')),
  mosaic: meta => isFilledArray(_.get(meta, 'images')),
  this_or_that: meta => isFilledArray(_.get(meta, 'associations')),
  at_the_place: meta => isFilledArray(_.get(meta, 'associations')),
  word_hunt: meta => isFilledArray(_.get(meta, 'words')),
  // `Sorted` separa por `;` uma string única, não uma lista.
  sorted: meta => isFilledString(_.get(meta, 'correct_words')),
  jigsaw: meta => isFilledString(_.get(meta, 'image.file.url')),
  // `File` aninha duas vezes: `meta.file` é o registro, `file.file.url` o arquivo.
  file: meta => isFilledString(_.get(meta, 'file.file.url')),
  video: meta => isFilledString(_.get(meta, 'video_url')),
  iframe: meta => isFilledString(_.get(meta, 'iframe')),
  // Só a múltipla escolha tem conteúdo obrigatório; a de texto se vira com o enunciado.
  question: meta =>
    _.get(meta, 'question_kind') === 'multiple_choice'
      ? isFilledArray(_.get(meta, 'alternatives'))
      : true,
  multiple_choice_question: meta =>
    isFilledArray(_.get(meta, 'alternatives'))
}

/**
 * Kinds que têm regra de conteúdo declarada.
 *
 * Exposto para a guarda de contrato conferir que cada um existe no catálogo: um
 * slug com erro de digitação aqui não quebra nada — só desliga a regra em
 * silêncio, e a atividade vazia volta a passar. Não é hipótese: o catálogo
 * carregava `AT_THE_Place` desde o commit inicial, e ninguém viu.
 */
export const CONTENT_RULE_KINDS = Object.keys(HAS_CONTENT)

/** O motivo de uma atividade não ser jogável, ou `null` quando ela é. */
export const resolveSkipReason = (
  activity: PlayabilityInput,
  knownKinds: ReadonlyArray<string>
): SkipReason | null => {
  if (!knownKinds.includes(activity.kind)) return 'kind_sem_componente'

  const hasContent = HAS_CONTENT[activity.kind]

  if (hasContent && !hasContent(activity.meta)) return 'sem_conteudo'

  return null
}

/**
 * Separa as atividades entre as que a execução monta e as que ela pula,
 * preservando a ordem original das jogáveis.
 */
export const resolvePlayableActivities = <T extends PlayabilityInput>({
  activities,
  knownKinds
}: {
  activities: ReadonlyArray<T>
  knownKinds: ReadonlyArray<string>
}): PlayabilityReport<T> => {
  const playable: T[] = []
  const unplayable: UnplayableActivity[] = []

  activities.forEach(activity => {
    const reason = resolveSkipReason(activity, knownKinds)

    if (reason === null) {
      playable.push(activity)
      return
    }

    unplayable.push({ id: activity.id, kind: activity.kind, reason })
  })

  return { playable, unplayable }
}

const REASON_LABEL: Record<SkipReason, string> = {
  kind_sem_componente: 'sem componente para o formato',
  sem_conteudo: 'sem conteúdo para montar'
}

/**
 * Resumo legível das atividades puladas, para registro no console — que o painel
 * de debug captura. É o "contabilizar": quem estiver diagnosticando um nível que
 * não conclui vê quantas atividades ficaram de fora e por quê, em vez de deduzir
 * do silêncio.
 *
 * Devolve `null` quando não há nada a relatar, para o chamador não registrar
 * linha vazia no caminho normal.
 */
export const describeUnplayableActivities = (
  unplayable: ReadonlyArray<UnplayableActivity>
): string | null => {
  if (unplayable.length === 0) return null

  const detail = unplayable
    .map(({ id, kind, reason }) => `#${id} (${kind}: ${REASON_LABEL[reason]})`)
    .join(', ')

  return `Level Play: ${unplayable.length} atividade(s) puladas — ${detail}`
}
